/**
 * useDraftList — Firestore subscription for user's drafts.
 * Track C3.
 */
"use client";

import { useState, useEffect } from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { getFirebaseAuth } from "@/platform/firebase/auth";
import { getUserDrafts, deleteDraft } from "@/modes/draft/api";
import type { Draft } from "@/modes/draft/types";

export function useDraftList() {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(getFirebaseAuth(), (u) => {
      setUser(u);
      if (!u) {
        setDrafts([]);
        setLoading(false);
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const data = await getUserDrafts(user!.uid);
        if (!cancelled) setDrafts(data);
      } catch (e) {
        console.error("Failed to load drafts:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [user]);

  const remove = async (draftId: string) => {
    await deleteDraft(draftId);
    setDrafts((prev) => prev.filter((d) => d.id !== draftId));
  };

  return { drafts, loading, remove };
}
